import { lazy, Suspense, useEffect, useRef, useState } from "react";
import type { Swiper as SwiperType } from "swiper";
import { type Product } from "@shared/schema";
import { cardImageSrcSet, CARD_IMAGE_SIZES, CARD_IMAGE_WIDTHS, THUMB_WIDTH, imageUrl } from "@shared/imageSizes";
import { Card, CardContent, CardFooter } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Minus, Plus, ZoomIn } from "lucide-react";
import { Dialog, DialogContent, DialogTitle, DialogTrigger } from "@/components/ui/dialog";

// The carousel library is loaded on demand, not with the initial page.
const ProductCarousel = lazy(() => import("@/components/product-carousel"));
const ProductLightboxCarousel = lazy(() => import("@/components/product-lightbox-carousel"));

interface ProductCardProps {
  product: Product;
  /** Size variants including the main product; omitted or length 1 = no size choice */
  variants?: Product[];
  quantities: Record<number, number>;
  onQuantityChange: (productId: number, quantity: number) => void;
  /** Card is visible at page load: load its first image immediately with high priority (LCP). */
  priority?: boolean;
}

/** Card title without the size suffix that variant rows carry in their name */
function baseName(product: Product) {
  const label = product.variantLabel;
  if (label && product.name.endsWith(` ${label}`)) {
    return product.name.slice(0, -label.length - 1);
  }
  return product.name;
}

export function ProductCard({ product, variants, quantities, onQuantityChange, priority = false }: ProductCardProps) {
  // React 18 doesn't know fetchPriority yet; the lowercase attribute is passed through to the DOM.
  const leadImageProps = priority
    ? ({ loading: "eager", fetchpriority: "high" } as Record<string, string>)
    : ({ loading: "lazy" } as Record<string, string>);
  const options = variants && variants.length > 1 ? variants : [product];
  const hasSizes = options.length > 1;
  const [selectedId, setSelectedId] = useState(options[0].id);
  const selected = options.find((v) => v.id === selectedId) ?? options[0];
  const quantity = quantities[selected.id] || 0;
  const anySelected = options.some((v) => (quantities[v.id] || 0) > 0);
  const title = hasSizes ? baseName(product) : product.name;
  const [lightboxOpen, setLightboxOpen] = useState(false);
  const [activeImage, setActiveImage] = useState(0);
  const [swiper, setSwiper] = useState<SwiperType | null>(null);
  const [expanded, setExpanded] = useState(false);
  const [clamped, setClamped] = useState(false);
  const descRef = useRef<HTMLParagraphElement>(null);
  const imageRef = useRef<HTMLDivElement>(null);
  // The carousel measures its size when it starts, which forces a layout pass.
  // Start it only when the card comes near the screen, not for all cards at page load.
  const [carouselReady, setCarouselReady] = useState(false);

  useEffect(() => {
    const el = imageRef.current;
    if (!el || product.images.length < 2 || carouselReady) return;
    const io = new IntersectionObserver((entries) => {
      if (entries.some((e) => e.isIntersecting)) {
        setCarouselReady(true);
        io.disconnect();
      }
    }, { rootMargin: "300px 0px" });
    io.observe(el);
    return () => io.disconnect();
  }, [product.images.length, carouselReady]);

  // Show "mehr" only when the description exceeds 8 lines.
  // The ResizeObserver also fires once initially, after layout, so no extra synchronous check is needed.
  useEffect(() => {
    const el = descRef.current;
    if (!el) return;
    const check = () => {
      if (!expanded) setClamped(el.scrollHeight > el.clientHeight + 1);
    };
    const ro = new ResizeObserver(check);
    ro.observe(el);
    return () => ro.disconnect();
  }, [expanded, product.description]);

  const handleIncrement = () => {
    onQuantityChange(selected.id, quantity + 1);
  };

  const handleDecrement = () => {
    if (quantity > 0) {
      onQuantityChange(selected.id, quantity - 1);
    }
  };

  return (
    <Card className="product-card group" data-selected={anySelected}>
      <CardContent className="p-0">
        {/* Image Gallery */}
        <div ref={imageRef} className="product-image relative aspect-[4/3]">
          {(() => {
            const staticImage = (
              <img
                sizes={CARD_IMAGE_SIZES}
                srcSet={cardImageSrcSet(product.images[0])}
                src={imageUrl(product.images[0], CARD_IMAGE_WIDTHS[1])}
                alt={product.images.length > 1 ? `${title} - Bild 1` : title}
                className="w-full h-full object-cover cursor-zoom-in"
                {...leadImageProps}
                onClick={() => setLightboxOpen(true)}
              />
            );
            if (product.images.length < 2 || !carouselReady) return staticImage;
            return (
              <Suspense fallback={staticImage}>
                <ProductCarousel
                  images={product.images}
                  title={title}
                  firstImageProps={leadImageProps}
                  onSwiper={setSwiper}
                  onSlideChange={setActiveImage}
                  onImageClick={() => setLightboxOpen(true)}
                />
              </Suspense>
            );
          })()}
          <Dialog open={lightboxOpen} onOpenChange={setLightboxOpen}>
            <DialogTrigger asChild>
              <Button
                size="icon"
                variant="secondary"
                className="absolute top-3 right-3 z-10"
                aria-label={`${title} - Bild vergrössern`}
                data-testid={`button-zoom-${product.id}`}
              >
                <ZoomIn className="w-4 h-4" />
              </Button>
            </DialogTrigger>
            <DialogContent className="max-w-4xl p-3 sm:p-4">
              <DialogTitle className="sr-only">{title}</DialogTitle>
              {product.images.length > 1 ? (
                <Suspense fallback={<img src={product.images[activeImage] ?? product.images[0]} alt={title} className="w-full h-auto max-h-[80vh] object-contain rounded-md" />}>
                  <ProductLightboxCarousel images={product.images} title={title} initialSlide={activeImage} />
                </Suspense>
              ) : (
                <img
                  src={product.images[0]}
                  alt={title}
                  className="w-full h-auto max-h-[80vh] object-contain rounded-md"
                />
              )}
            </DialogContent>
          </Dialog>
        </div>

        {/* Thumbnails (space reserved on every card so titles line up) */}
        <div className="product-thumbs" data-testid={`thumbs-${product.id}`}>
          {product.images.length > 1 && product.images.map((image, index) => (
            <button
              key={index}
              type="button"
              className="product-thumb"
              data-active={index === activeImage}
              aria-label={`${title} - Bild ${index + 1} anzeigen`}
              aria-current={index === activeImage}
              onClick={() => { setCarouselReady(true); swiper?.slideTo(index); }}
            >
              <img src={imageUrl(image, THUMB_WIDTH)} alt="" loading="lazy" />
            </button>
          ))}
        </div>

        {/* Product Info */}
        <div className="product-copy">
          <h4 className="product-name">
            {title}
          </h4>
          <p
            ref={descRef}
            className={`text-sm text-muted-foreground leading-relaxed whitespace-pre-line ${expanded ? "" : "line-clamp-[8]"}`}
          >
            {product.description}
          </p>
          {(clamped || expanded) && (
            <button
              type="button"
              className="product-more"
              onClick={() => setExpanded(!expanded)}
              aria-expanded={expanded}
              data-testid={`button-more-${product.id}`}
            >
              {expanded ? "weniger" : "mehr"}
            </button>
          )}
          <div className="mb-4" />
          {hasSizes && (
            <div className="product-sizes" role="radiogroup" aria-label={`${title} - Grösse`}>
              {options.map((v) => {
                const q = quantities[v.id] || 0;
                return (
                  <button
                    key={v.id}
                    type="button"
                    role="radio"
                    aria-checked={v.id === selected.id}
                    className="product-size"
                    data-active={v.id === selected.id}
                    onClick={() => setSelectedId(v.id)}
                    data-testid={`button-size-${v.id}`}
                  >
                    {v.variantLabel}
                    {q > 0 && <span className="product-size-count" aria-label={`${q} im Warenkorb`}>{q}</span>}
                  </button>
                );
              })}
            </div>
          )}
          <p className="product-price">
            CHF {parseFloat(selected.price).toFixed(2)}
          </p>
        </div>
      </CardContent>

       <CardFooter className="px-7 pb-7 pt-0">
        {/* Quantity Selector */}
        <div className="quantity-well flex items-center justify-between gap-3 w-full">
          <Button
            size="icon"
            variant="outline"
            onClick={handleDecrement}
            disabled={quantity === 0}
            data-testid={`button-decrease-${selected.id}`}
            aria-label={`− ${selected.name}`}
            className="w-10 h-10"
          >
            <Minus className="w-4 h-4" />
          </Button>
          <div className="w-16 text-center">
            <span key={quantity} className="quantity-number" aria-live="polite" data-testid={`quantity-${selected.id}`}>
              {quantity}
            </span>
          </div>
          <Button
            size="icon"
            variant="outline"
            onClick={handleIncrement}
            data-testid={`button-increase-${selected.id}`}
            aria-label={`+ ${selected.name}`}
            className="w-10 h-10"
          >
            <Plus className="w-4 h-4" />
          </Button>
        </div>
      </CardFooter>
    </Card>
  );
}
